import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import FormInput from "pages/FormInput";
import React, { useState } from "react";
import {
  editCivilCaseAppeal2,
  editCivilCaseResolution2,
} from "services/civilCaseComplaintDetailService";
import { editCivilCaseComplaint } from "services/civilCaseComplaintService";
import {
  CivilCaseAppeald,
  CivilCaseComplaint,
  CivilCaseResolutions,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  resolution: CivilCaseResolutions;
  appeal?: CivilCaseAppeald;
  complaint?: CivilCaseComplaint;
  onClose: (refetch: boolean) => void;
};

const CivilCaseRegistrarForm: React.FC<Props> = ({
  resolution,
  appeal,
  complaint,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const [formState, setFormState] = useState<CivilCaseResolutions>(resolution);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  const validateForm = () => {
    if (!formState.registration_number)
      return "Registration Number is Required.";
    if (!formState.registration_date) return "Registration Date is Required.";
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    editCivilCaseResolution2({
      id: resolution?.id,
      registration_number: formState?.registration_number,
      registration_date: formState?.registration_date,
      complaint: resolution?.complaint,
      appealed_complaint: appeal?.id,
    })
      .then(() => {
        if (resolution?.complaint && complaint) {
          editCivilCaseComplaint({
            id: resolution?.complaint,
            status: "resolved",
            admin_unit_id: complaint?.org_unit_id
              ? undefined
              : loginData?.structure?.admin_unit?.parent_unit,
            registration_number: formState?.registration_number,
            registration_date: formState?.registration_date,
          });
        }
        if (appeal) {
          editCivilCaseAppeal2({
            id: appeal?.id,
            complaint: appeal?.complaint,
            parent_appeal: appeal?.parent_appeal,
            registration_number: formState?.registration_number,
            registration_date: formState?.registration_date,
            status: "resolved",
            admin_unit_id: appeal?.org_unit_id
              ? loginData?.admin_unit
              : loginData?.structure?.admin_unit?.parent_unit,
          });
        }
        showTooltip("Successfully Submitted!", "success");
        setFormState({});
        onClose(true);
      })
      .catch((err) => {
        showTooltip(errorHandlerOnForm(err?.response?.data), "error");
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div
        className="relative w-full max-w-xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto"
        style={{ maxHeight: "max-content" }}
      >
        <form onSubmit={handleSubmit}>
          <span className="text-[24px] font-semibold ">Add Registration</span>
          <div className="grid grid-cols-1 gap-4 my-4">
            <FormInput
              label="Registration Number"
              type="text"
              value={formState.registration_number ?? ""}
              onChange={handleChange}
              name="registration_number"
            />
            <FormInput
              label="Registration Date"
              type="date"
              value={
                formState.registration_date
                  ? new Date(formState.registration_date)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={handleChange}
              name="registration_date"
            />
          </div>
          <div className="flex flex-row mt-4 gap-3">
            <button
              type="reset"
              disabled={loading}
              className="flex-1 border border-[#212121] py-3 text-[#747474]  cursor-pointer font-[500] text-base rounded-md hover:bg-[#D4D4D4]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 items-center bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg flex justify-center"
              value={"Submit"}
            >
              {loading ? (
                <>
                  <span className="animate-spin">{LoadingIcon}</span>
                  Processing...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CivilCaseRegistrarForm;
